import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import TareHistoryRow from "./TareHistoryRow";
import { DataTable } from "../../widgets";
import {
  DataTableLoading,
  DataTableRecordsNotFound,
} from "../../widgets/DataTable";
import FilterPipeDropDown from "./FilterPipeDropDown";
import {
  getTHFilter,
  removeItem,
  setTHFilter,
  tareHistoryFilterName,
} from "../../../helpers/session";

const TareLoadHistory = ({
  tareLoadList = [],
  data,
  getTareHistory,
  tareLoadParams,
  setFilteredTareList,
  weightShortName,
  unit,
}) => {
  const searchParams = useSelector((state) => state.analyze.searchParams);
  const [sortedDataList, setSortedDataList] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: null,
    direction: null,
  });
  const { rangeId } = searchParams;
  const weightsKeys = ["load_gross_weight", "light_weight", "load_capacity"];

  const { data: tareLoads, isRequesting } = useMemo(() => {
    const { data, isRequesting } = tareLoadList;
    setSortedDataList(data);
    return { data, isRequesting };
  }, [tareLoadList]);

  const customColHeaders = data.tareLoads.header
    .filter((item) => item.show)
    .map((item) => item.key);
  const customColumns = ["id", ...customColHeaders];

  // const nonEmptyColumns = useMemo(() => {
  //     const newCustomColumns = [];

  //     for (const item of tareLoads) {
  //         if (newCustomColumns.length === customColHeaders.length) break;

  //         for (const field of customColHeaders) {
  //             if (newCustomColumns.indexOf(field) >= 0) continue;
  //             else if (item[field] !== "Unknown" && item[field] !== "" && item[field] !== null && item[field] !== 0) newCustomColumns.push(field);
  //             else newCustomColumns.push(customColHeaders);
  //         };
  //     };

  //     return [...customColHeaders, 'id']
  // }, [tareLoads]);

  const header = data.tareLoads.header;

  useEffect(() => {
    removeItem(tareHistoryFilterName);
    setFilters({});
  }, [rangeId]);

  const getUniqueOptions = (data, key) => {
    return [...new Set(data.map((item) => item[key]))];
  };

  useEffect(() => {
    if (tareLoads.length > 0) {
      const initialFilters = tareLoads.reduce((acc, item) => {
        Object.keys(item).forEach((key) => {
          if (!acc[key]) {
            acc[key] = [];
          }
        });
        return acc;
      }, {});
      const sessionFilter = getTHFilter();
      const fitlerData =
        sessionFilter && Object.entries(sessionFilter).length > 0
          ? sessionFilter
          : initialFilters;
      setFilters(fitlerData);
    }
  }, [tareLoads]);

  const handleFilterChange = (filterKey, value) => {
    setFilters((prevFilters) => {
      let newFilterValues;
      if (weightsKeys.includes(filterKey)) {
        newFilterValues = prevFilters[filterKey].includes(value)
          ? prevFilters[filterKey].filter((item) => item !== value)
          : [...(prevFilters[filterKey] || []), value];
      } else {
        newFilterValues = prevFilters[filterKey].includes(value)
          ? prevFilters[filterKey].filter((item) => item !== value)
          : [...(prevFilters[filterKey] || []), value];
      }

      return {
        ...prevFilters,
        [filterKey]: newFilterValues,
      };
    });
  };

  useEffect(() => {
    let filteredData = [...tareLoads];

    Object.keys(filters).forEach((filterKey) => {
      if (filters[filterKey].length > 0) {
        if (weightsKeys.includes(filterKey)) {
          const filterRanges = getFilterRange(weightShortName);

          filteredData = filteredData.filter((row) => {
            return filters[filterKey].some((selectedRange) => {
              const weightValue = unit.toRoundOfWeight(row[filterKey]);

              switch (selectedRange) {
                case `Below ${filterRanges[0].label} (${weightShortName})`:
                  return weightValue <= filterRanges[0].value;
                case `${filterRanges[0].label} - ${filterRanges[1].label} (${weightShortName})`:
                  return (
                    weightValue > filterRanges[0].value &&
                    weightValue <= filterRanges[1].value
                  );
                case `${filterRanges[1].label} - ${filterRanges[2].label} (${weightShortName})`:
                  return (
                    weightValue > filterRanges[1].value &&
                    weightValue <= filterRanges[2].value
                  );
                case `${filterRanges[2].label} (${weightShortName}) & Above`:
                  return weightValue > filterRanges[2].value;
                default:
                  return false;
              }
            });
          });
        } else {
          filteredData = filteredData.filter((row) =>
            filters[filterKey].includes(row[filterKey])
          );
        }
      }
    });

    if (sortConfig.key) {
      filteredData.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === "asc" ? 1 : -1;
        }
        return 0;
      });
    }

    setSortedDataList(filteredData);
    setFilteredTareList(filteredData);
    setTHFilter(filters);
  }, [filters, sortConfig, tareLoads, weightShortName]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }

    setSortConfig({ key, direction });
  };

  const renderSortOrder = (sortField) => {
    const { direction, key } = sortConfig;
    if (sortField !== key || direction === null)
      return (
        <i
          className="fa fa-caret-down sort-direction hidden"
          aria-hidden="true"
        />
      );
    if (direction === "asc")
      return (
        <i className="fa fa-caret-down sort-direction" aria-hidden="true" />
      );
    if (direction === "desc")
      return <i className="fa fa-caret-up sort-direction" aria-hidden="true" />;
  };

  const getFilterLabelList = useCallback((keyName) => {
    const range = getFilterRange(keyName);
    if (!range) return [];
    else
      return [
        `Below ${range[0].label} (${weightShortName})`,
        `${range[0].label} - ${range[1].label} (${weightShortName})`,
        `${range[1].label} - ${range[2].label} (${weightShortName})`,
        `${range[2].label} (${weightShortName}) & Above`,
      ];
  }, []);

  const getFilterRange = (keyName) => {
    switch (keyName) {
      case "lbs":
        return [
          { value: 25000, label: "25000" },
          { value: 50000, label: "50000" },
          { value: 100000, label: "100000" },
        ];
      case "sacks":
        return [
          { value: 250, label: "250" },
          { value: 500, label: "500" },
          { value: 1000, label: "1000" },
        ];
      case "tons":
      case "tonnes":
        return [
          { value: 15, label: "15" },
          { value: 30, label: "30" },
          { value: 50, label: "50" },
        ];
      default:
        return null;
    }
  };

  const filterKeys = header
    .filter((e) => e.key !== "id")
    .map((data) => data.key);
  const filterOptions = filterKeys.reduce((acc, key) => {
    if (weightsKeys.includes(key)) {
      acc[key] = getFilterLabelList(weightShortName);
    } else {
      acc[key] = getUniqueOptions(tareLoads, key);
    }
    return acc;
  }, {});

  return (
    <div style={{ marginTop: "20px" }}>
      <DataTable>
        <DataTable.Header className={"tare_tableHeader"}>
          <DataTable.Row>
            {header
              .filter((e) =>
                isRequesting || (tareLoads && tareLoads.length === 0)
                  ? true
                  : customColumns.includes(e.key)
              )
              .map((e, index) => (
                <DataTable.Cell key={index} width={e.width} id={e.key}>
                  <div className="loadHistory_headerData">
                    <span
                      className="sort-column"
                      onClick={() => e.isSort && handleSort(e.key)}
                    >
                      <span>{e.label}</span>
                      {e.isSort ? <span>{renderSortOrder(e.key)}</span> : null}
                    </span>
                    {e.isPipeFilter && (
                      <FilterPipeDropDown
                        key={e.key}
                        data={sortedDataList}
                        options={filterOptions[e.key]}
                        selected={filters[e.key] || []}
                        filterKey={e.key}
                        handleFilterChange={handleFilterChange}
                        weightsKeys={weightsKeys}
                      />
                    )}
                  </div>
                </DataTable.Cell>
              ))}
            <DataTable.Cell width={"80px"}>Action</DataTable.Cell>
          </DataTable.Row>
        </DataTable.Header>

        <DataTable.Body>
          {sortedDataList.length > 0
            ? sortedDataList.map((e, index) => (
                <TareHistoryRow
                  data={e}
                  getTareHistory={getTareHistory}
                  key={index}
                  nonEmptyColumns={customColHeaders}
                  index={index + 1}
                  isRequesting={false}
                  tareLoadParamsList={tareLoadParams}
                />
              ))
            : null}
        </DataTable.Body>
      </DataTable>

      <div className="info-text">
        {isRequesting && <DataTableLoading />}
        {!isRequesting && tareLoads && tareLoads.length === 0 && (
          <DataTableRecordsNotFound children={"No Recorded Tare Loads"} />
        )}
      </div>
    </div>
  );
};

export default memo(TareLoadHistory);
